import React from "react";
import { Badge } from "@/components/ui/badge";
import { 
  Plane, Car, Hotel, Utensils, MapPinned, Music, Check, Clock,
  ArrowRight, Timer
} from "lucide-react";
import { format, addDays } from "date-fns";
import { FlightSearchLinks } from "./FlightSearchLinks";
import { getAirportTimezoneAbbr, type Airport } from "@/lib/airports";
import { calculateLandingTime, type FlightTimeResult } from "@/lib/flightCalculations";

interface TimelineEvent {
  time: number;
  label: string;
  icon: React.ElementType;
  highlight?: boolean;
  muted?: boolean;
}

interface TravelTimelineProps {
  originCode?: string;
  arrCode?: string;
  arrName?: string;
  depCode?: string;
  flightDurationMinutes: number;
  driveToVenueMinutes: number;
  driveToAirportMinutes: number;
  distanceToVenue?: number;
  distanceToAirport?: number;
  eventStartMinutes: number;
  eventEndMinutes: number;
  eventOccasion: string;
  eventDate?: Date;
  recommendedArrivalDate?: Date;
  recommendedDepartureDate?: Date;
  isSameDayArrival: boolean;
  isSameDayDeparture: boolean;
  useRedEyeInbound: boolean;
  inboundDepartureMinutes?: number;
  flightTime?: FlightTimeResult | null;
  isRoundTrip: boolean;
}

// Format time from minutes to 12h format
const formatTime = (minutes: number): string => {
  const normalized = ((minutes % 1440) + 1440) % 1440;
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${m.toString().padStart(2, '0')} ${period}`;
};

// Format duration in minutes to "Xh Xm" format
const formatDuration = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

// Timeline constants
const BAGGAGE_TIME = 45;
const CHECKIN_TIME = 20;
const FRESHEN_TIME = 20;
const MEAL_TIME = 20;
const SETUP_TIME = 120;
const PACKUP_TIME = 30;
const AIRPORT_BUFFER = 120;
const HOTEL_TO_VENUE = 5;

export function TravelTimeline({
  originCode,
  arrCode,
  arrName,
  depCode,
  flightDurationMinutes,
  driveToVenueMinutes,
  driveToAirportMinutes,
  distanceToVenue,
  distanceToAirport,
  eventStartMinutes,
  eventEndMinutes,
  eventOccasion,
  eventDate,
  recommendedArrivalDate,
  recommendedDepartureDate,
  isSameDayArrival,
  isSameDayDeparture,
  useRedEyeInbound,
  inboundDepartureMinutes,
  flightTime,
  isRoundTrip,
}: TravelTimelineProps) {
  const destTz = arrCode ? getAirportTimezoneAbbr(arrCode) : '';
  const flightHours = Math.floor(flightDurationMinutes / 60);
  const flightMins = flightDurationMinutes % 60;

  // Calculate landing time
  let takeoffTime = inboundDepartureMinutes ?? 0;
  if (takeoffTime < 0) takeoffTime = 1440 + takeoffTime;
  
  let landingMinutes = 0;
  let landingDayOffset = 0;
  
  if (flightTime) {
    const landing = calculateLandingTime(takeoffTime, flightTime);
    landingMinutes = landing.landingMinutes;
    landingDayOffset = landing.dayOffset;
  } else {
    landingMinutes = takeoffTime + flightDurationMinutes;
  }

  // Calculate departure date for timeline
  const departureDay = isSameDayArrival && eventDate 
    ? addDays(eventDate, -1) 
    : recommendedArrivalDate;
  
  const landingDate = departureDay 
    ? addDays(departureDay, landingDayOffset || 0)
    : eventDate;

  // Forward timeline from landing
  const leaveAirport = landingMinutes + BAGGAGE_TIME;
  const arriveHotel = leaveAirport + driveToVenueMinutes;
  const readyTime = arriveHotel + CHECKIN_TIME + FRESHEN_TIME + MEAL_TIME;
  const arriveVenue = eventStartMinutes - SETUP_TIME;
  
  // Post-event timeline
  const leaveVenue = eventEndMinutes + PACKUP_TIME;
  const returnHotel = leaveVenue + HOTEL_TO_VENUE;
  
  // Red-eye departure
  const redeyeAirport = leaveVenue + driveToAirportMinutes;
  const redeyeFlight = redeyeAirport + AIRPORT_BUFFER;
  
  // Next-day departure
  const checkoutTime = 11 * 60; // 11 AM
  const nextDayAirport = checkoutTime + driveToAirportMinutes;
  const nextDayFlight = nextDayAirport + AIRPORT_BUFFER;

  return (
    <div className="space-y-4">
      {/* Flight Summary Card */}
      <div className="p-3 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Plane className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm">
              {originCode} <ArrowRight className="h-3 w-3 inline mx-1" /> {arrCode}
            </span>
            <Badge variant="secondary" className="text-[9px]">
              ~{flightHours}h {flightMins > 0 ? `${flightMins}m` : ''}
            </Badge>
          </div>
          {useRedEyeInbound && (
            <Badge className="text-[9px] bg-amber-500/20 text-amber-700 border-amber-500/30">
              Red-eye
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-3">{arrName}</p>
        
        {originCode && arrCode && (
          <FlightSearchLinks
            origin={originCode}
            destination={arrCode}
            departureDate={departureDay}
            returnDate={isRoundTrip ? recommendedDepartureDate : undefined}
            label={isRoundTrip ? "Search flights" : "Search outbound"}
            takeoffWindowStart={takeoffTime}
            takeoffWindowEnd={Math.min(takeoffTime + 120, 1439)}
            landingWindowEnd={landingMinutes}
            returnTakeoffWindowStart={isSameDayDeparture ? redeyeFlight : nextDayFlight}
            returnTakeoffWindowEnd={isSameDayDeparture ? redeyeFlight + 240 : nextDayFlight + 240}
          />
        )}
      </div>

      {/* Condensed Timeline */}
      <div className="space-y-3">
        {/* Arrival Day */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-[10px] font-medium">
              {landingDate ? format(landingDate, 'EEE, MMM d') : 'Arrival'}
            </Badge>
            {isSameDayArrival && (
              <span className="text-[10px] text-amber-600 font-medium">Same-day arrival</span>
            )}
          </div>
          
          <div className="grid grid-cols-[4rem_1fr] gap-x-2 gap-y-0.5 text-xs ml-1 pl-3 border-l-2 border-primary/30">
            {/* Landing → Ready */}
            <span className="text-muted-foreground tabular-nums">{formatTime(landingMinutes)}</span>
            <span className="flex items-center gap-1.5">
              <Plane className="h-3 w-3 text-primary shrink-0" />
              Land at {arrCode} {destTz && <span className="text-[9px] text-muted-foreground">({destTz})</span>}
            </span>
            
            <span className="text-muted-foreground tabular-nums">{formatTime(leaveAirport)}</span>
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Clock className="h-3 w-3 shrink-0" />
              Baggage → Drive ({formatDuration(driveToVenueMinutes)})
            </span>
            
            <span className="text-muted-foreground tabular-nums">{formatTime(arriveHotel)}</span>
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Hotel className="h-3 w-3 shrink-0" />
              Hotel check-in & prep
            </span>
            
            {/* Event block */}
            <div className="col-span-2 my-1.5 py-2 px-2 -ml-2 bg-primary/10 rounded-md">
              <div className="flex items-center gap-2 mb-1">
                <Music className="h-3.5 w-3.5 text-primary" />
                <span className="font-semibold text-primary text-sm">{eventOccasion}</span>
              </div>
              <div className="flex items-center gap-3 text-[11px]">
                <span className="flex items-center gap-1">
                  <MapPinned className="h-3 w-3" />
                  {formatTime(arriveVenue)} setup
                </span>
                <span className="text-primary font-medium">
                  {formatTime(eventStartMinutes)} – {formatTime(eventEndMinutes)}
                </span>
              </div>
            </div>
            
            <span className="text-muted-foreground tabular-nums">{formatTime(leaveVenue)}</span>
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Timer className="h-3 w-3 shrink-0" />
              Pack up & wrap
            </span>
          </div>
        </div>

        {/* Departure */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-[10px] font-medium">
              {isSameDayDeparture 
                ? (eventDate ? format(eventDate, 'EEE, MMM d') : 'Same day') 
                : (recommendedDepartureDate ? format(recommendedDepartureDate, 'EEE, MMM d') : 'Departure')}
            </Badge>
            <span className="text-[10px] text-muted-foreground">
              {isSameDayDeparture ? 'Direct to airport' : 'Checkout & fly'}
            </span>
          </div>
          
          <div className="grid grid-cols-[4rem_1fr] gap-x-2 gap-y-0.5 text-xs ml-1 pl-3 border-l-2 border-muted-foreground/30">
            {isSameDayDeparture ? (
              <>
                <span className="text-muted-foreground tabular-nums">{formatTime(returnHotel)}</span>
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Car className="h-3 w-3 shrink-0" />
                  Drive to {depCode || arrCode} ({formatDuration(driveToAirportMinutes)})
                </span>
                
                <span className="text-primary tabular-nums font-medium">{formatTime(redeyeFlight)}</span>
                <span className="flex items-center gap-1.5 text-primary font-medium">
                  <Plane className="h-3 w-3 shrink-0" />
                  Earliest departure
                </span>
              </>
            ) : (
              <>
                <span className="text-muted-foreground tabular-nums">{formatTime(checkoutTime)}</span>
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Hotel className="h-3 w-3 shrink-0" />
                  Checkout
                </span>
                
                <span className="text-muted-foreground tabular-nums">{formatTime(nextDayAirport)}</span>
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Car className="h-3 w-3 shrink-0" />
                  Drive to {depCode || arrCode} ({formatDuration(driveToAirportMinutes)})
                </span>
                
                <span className="text-primary tabular-nums font-medium">{formatTime(nextDayFlight)}</span>
                <span className="flex items-center gap-1.5 text-primary font-medium">
                  <Plane className="h-3 w-3 shrink-0" />
                  Earliest departure
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
